// src/pages/UserProfile.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  BadgeOutlined,
  BusinessOutlined,
  CameraAltOutlined,
  DeleteOutline,
  EmailOutlined,
  LockOutlined,
  PersonOutline,
  PlaceOutlined,
  Visibility,
  VisibilityOff,
  ArrowBackRounded,
  CloseRounded,
} from "@mui/icons-material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import api from "../lib/api";
import { clearTokens, getDecodedAccess } from "../lib/auth";
import { getRoleDisplayName } from "../lib/roleLabels";
import AppSnackbar from "../components/AppSnackbar";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import {
  IMAGE_UPLOAD_SIZE_ERROR,
  imageExceedsUploadLimit,
} from "../utils/imageUpload";
import { authFieldSx } from "./authFieldStyles";
import { passwordToggleSx } from "./authPasswordToggleStyles";

const COUNTRY_OPTIONS = ["Egypt", "Jordan", "Morocco", "Tunisia"];

const regionMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#fff",
      border: "1px solid #e8e8e8",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
      "& .MuiMenuItem-root": {
        backgroundColor: "#fff !important",
        "&:hover, &.Mui-focusVisible": {
          backgroundColor: "#fffaf0 !important",
        },
        "&.Mui-selected": {
          backgroundColor: "#fff7d6 !important",
        },
        "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
          backgroundColor: "#ffefb0 !important",
        },
      },
    },
  },
};

const yellowButtonSx = {
  "&&": {
    minHeight: 44,
    px: 3,
    bgcolor: "#f7b900",
    backgroundImage: "none",
    color: "#312600",
    borderColor: "#f7b900",
    boxShadow: "none",
    fontWeight: 600,
    textTransform: "none",
  },
  "&&:hover, &&:focus-visible": {
    bgcolor: "#dca600",
    backgroundImage: "none",
    color: "#312600",
    borderColor: "#dca600",
    boxShadow: "none",
  },
};

const disabledAccountFieldSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "#f3f4f6",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
    "& fieldset": { borderColor: "#d1d5db" },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#6b7280",
  },
  "& .MuiFormHelperText-root": {
    color: "#6b7280",
  },
};

function FieldLabel({ icon, label }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
      {icon ? React.cloneElement(icon, { sx: { fontSize: 18 } }) : null}
      <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{label}</Typography>
    </Stack>
  );
}

function CardHeaderBlock({
  avatarUrl,
  initials,
  onPickAvatar,
  onRemoveAvatar,
  hasUnsavedChanges,
}) {
  return (
    <Stack alignItems="center" spacing={2.25}>
      <Box sx={{ position: "relative" }}>
        <Avatar
          src={avatarUrl || undefined}
          sx={{
            width: 96,
            height: 96,
            bgcolor: "#f7b900",
            color: "#fff",
            fontSize: 30,
            fontWeight: 800,
            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.18)",
          }}
        >
          {initials}
        </Avatar>
        {avatarUrl ? (
          <IconButton
            size="small"
            aria-label="Remove avatar"
            title="Remove avatar"
            onClick={onRemoveAvatar}
            sx={{
              position: "absolute", top: -8, right: -8, width: 28, height: 28,
              bgcolor: "#fff", color: "#dc2626", border: "1px solid #e5e7eb",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.18)",
              "&:hover": { bgcolor: "#fff1f2", color: "#b91c1c" },
            }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        ) : null}
      </Box>

      <Box sx={{ textAlign: "center" }}>
        <Button
          component="label"
          startIcon={<CameraAltOutlined />}
          sx={{
            color: "#111",
            textTransform: "none",
            fontWeight: 800,
            "&:hover": { bgcolor: "#fffaf0" },
          }}
        >
          Choose avatar
          <input hidden accept="image/*" type="file" onChange={onPickAvatar} />
        </Button>
        <Typography sx={{ mt: 1, color: "text.secondary", fontSize: 12 }}>
          Maximum file size: 100 KB.
        </Typography>
        {hasUnsavedChanges ? (
          <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: 12 }}>
            Your changes will be saved when you press "Save".
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

export default function UserProfilePage() {
  const navigate = useNavigate();
  const decoded = getDecodedAccess();
  const userId = decoded?.user_id;

  const [loading, setLoading] = React.useState(true);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [username, setUsername] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [roleName, setRoleName] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [headline, setHeadline] = React.useState("");
  const [bio, setBio] = React.useState("");
  const [savedProfile, setSavedProfile] = React.useState(null);

  const [avatarUrl, setAvatarUrl] = React.useState("");
  const [avatarFile, setAvatarFile] = React.useState(null);
  const [avatarPreview, setAvatarPreview] = React.useState("");
  const [avatarRemoved, setAvatarRemoved] = React.useState(false);

  const [password2, setPassword2] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [openPassword, setOpenPassword] = React.useState(false);

  const [fieldErrors, setFieldErrors] = React.useState({});
  const [openDelete, setOpenDelete] = React.useState(false);

  React.useEffect(() => {
    if (!userId) {
      navigate("/signin");
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/${userId}/getuserprofile`);
        const data = res.data || {};

        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setUsername(data.username || "");
        setEmail(data.email || "");
        setRoleName(getRoleDisplayName(data.role, data.role_name));
        setLocation(data.location || "");
        setHeadline(data.headline || data.organization || "");
        setBio(data.bio || "");
        setSavedProfile({
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          location: data.location || "",
          headline: data.headline || data.organization || "",
          bio: data.bio || "",
        });
        setAvatarUrl(data.avatar || data.avatar_url || "");
        setAvatarRemoved(false);

        setPassword2("");
        setOpenPassword(false);
      } catch (err) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.error || "Failed to load profile.",
          severity: "error",
        });
        if (err?.response?.status === 401) navigate("/signin");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, userId]);

  const validateInputs = () => {
    const errors = {};
    if (!firstName.trim()) errors.firstName = "First name is required";
    if (!lastName.trim()) errors.lastName = "Last name is required";
    if (!location) errors.location = "Please select a region";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onPickAvatar = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (imageExceedsUploadLimit(file)) {
      event.target.value = "";
      setSnackbar({
        open: true,
        message: IMAGE_UPLOAD_SIZE_ERROR,
        severity: "error",
      });
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setAvatarRemoved(false);
  };

  const onRemoveAvatar = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview("");
    setAvatarRemoved(true);
  };

  const hasUnsavedChanges = Boolean(
    savedProfile &&
      (firstName !== savedProfile.firstName ||
        lastName !== savedProfile.lastName ||
        location !== savedProfile.location ||
        headline !== savedProfile.headline ||
        bio !== savedProfile.bio ||
        avatarFile ||
        avatarRemoved)
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateInputs() || !userId) return;

    try {
      if (avatarFile) {
        const formData = new FormData();
        formData.append("first_name", firstName);
        formData.append("last_name", lastName);
        formData.append("location", location);
        formData.append("headline", headline);
        formData.append("bio", bio);
        formData.append("avatar", avatarFile);

        await api.put(`/${userId}/updateUserProfile`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setAvatarUrl(avatarPreview || avatarUrl);
        setAvatarFile(null);
        setAvatarPreview("");
        setAvatarRemoved(false);
      } else {
        const payload = {
          first_name: firstName,
          last_name: lastName,
          location,
          headline,
          bio,
          remove_avatar: avatarRemoved,
        };

        await api.put(`/${userId}/updateUserProfile`, payload);
        if (avatarRemoved) {
          setAvatarUrl("");
          setAvatarRemoved(false);
        }
      }

      setSnackbar({
        open: true,
        message: "Profile updated successfully.",
        severity: "success",
      });
      setSavedProfile({ firstName, lastName, location, headline, bio });
      window.dispatchEvent(new CustomEvent("seeds-profile-updated", {
        detail: {
          first_name: firstName,
          last_name: lastName,
          email,
          username: username || decoded?.username || email,
          avatar: avatarRemoved ? "" : (avatarPreview || avatarUrl),
        },
      }));
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Error updating profile.",
        severity: "error",
      });
    }
  };

  const handlePasswordSave = async () => {
    if (!userId) return;
    if (!password2 || password2.length < 6) {
      setFieldErrors((prev) => ({
        ...prev,
        password: "Password must be at least 6 characters",
      }));
      setSnackbar({
        open: true,
        message: "Password must be at least 6 characters.",
        severity: "error",
      });
      return;
    }

    try {
      await api.put(`/${userId}/updateUserProfile`, {
        first_name: firstName,
        last_name: lastName,
        location,
        headline,
        bio,
        change_password: true,
        newPassword: password2,
      });
      setOpenPassword(false);
      setPassword2("");
      setShowPassword(false);
      setFieldErrors((prev) => ({ ...prev, password: "" }));
      setSnackbar({
        open: true,
        message: "Password updated successfully.",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Could not update password.",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/${userId}/deleteAccount`);
      clearTokens();
      navigate("/signup");
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || "Failed to delete account.",
        severity: "error",
      });
    } finally {
      setOpenDelete(false);
    }
  };

  const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "U";
  const profileHandle = username || decoded?.username;

  if (loading) {
    return <Typography sx={{ p: 3 }}>Loading...</Typography>;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ maxWidth: 760, mx: "auto" }}>
        <Box sx={{ mb: 3.5 }}>
          <Button
            startIcon={<ArrowBackRounded />}
            onClick={() =>
              navigate(profileHandle ? `/u/${encodeURIComponent(profileHandle)}` : "/")
            }
            color="inherit"
            sx={{ minWidth: 0, px: 0.5, mb: 1.25, textTransform: "none" }}
          >
            Back to profile
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.75 }}>
            My Account
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            Manage your profile and account settings
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Card
            sx={{
              p: { xs: 2.5, md: 3 },
              bgcolor: "#fff",
              border: "1px solid #e4e4e4",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
            }}
          >
            <CardHeaderBlock
              avatarUrl={avatarRemoved ? "" : (avatarPreview || avatarUrl)}
              initials={initials}
              onPickAvatar={onPickAvatar}
              onRemoveAvatar={onRemoveAvatar}
              hasUnsavedChanges={hasUnsavedChanges}
            />

            <Divider sx={{ my: 3 }} />

            <Stack spacing={2.25}>
              <Box>
                <FieldLabel icon={<PersonOutline />} label="First name" />
                <TextField
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  error={Boolean(fieldErrors.firstName)}
                  helperText={fieldErrors.firstName}
                  sx={authFieldSx}
                />
              </Box>

              <Box>
                <FieldLabel icon={<PersonOutline />} label="Last name" />
                <TextField
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  error={Boolean(fieldErrors.lastName)}
                  helperText={fieldErrors.lastName}
                  sx={authFieldSx}
                />
              </Box>

              <Box>
                <FieldLabel icon={<EmailOutlined />} label="Email" />
                <TextField fullWidth value={email} disabled sx={disabledAccountFieldSx} />
              </Box>

              <Box>
                <FieldLabel icon={<BadgeOutlined />} label="Role" />
                <TextField
                  fullWidth
                  value={roleName || ""}
                  disabled
                  placeholder="e.g., Agricultural Researcher"
                  helperText="Role is set at signup and cannot be changed."
                  sx={disabledAccountFieldSx}
                />
              </Box>

              <Box>
                <FieldLabel icon={<PlaceOutlined />} label="Region" />
                <FormControl fullWidth error={Boolean(fieldErrors.location)}>
                  <InputLabel
                    id="location-label"
                    sx={{ color: "#b47d00", "&.Mui-focused": { color: "#a56f00" } }}
                  >
                    Select region...
                  </InputLabel>
                  <Select
                    labelId="location-label"
                    label="Select region..."
                    IconComponent={KeyboardArrowDownRoundedIcon}
                    MenuProps={regionMenuProps}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    sx={authFieldSx}
                  >
                    {COUNTRY_OPTIONS.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.location ? (
                    <FormHelperText>{fieldErrors.location}</FormHelperText>
                  ) : null}
                </FormControl>
              </Box>

              <Box>
                <FieldLabel icon={<BusinessOutlined />} label="Organization / Headline" />
                <TextField
                  fullWidth
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Your organization or headline"
                  sx={authFieldSx}
                />
              </Box>

              <Box>
                <FieldLabel label="Bio" />
                <TextField
                  fullWidth
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell us about yourself and your research interests..."
                  multiline
                  minRows={5}
                  sx={authFieldSx}
                />
              </Box>
            </Stack>
          </Card>

          <Card
            sx={{
              mt: 3,
              p: { xs: 2.5, md: 3 },
              bgcolor: "#fff",
              border: "1px solid #e4e4e4",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <LockOutlined sx={{ fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Security
              </Typography>
            </Stack>

            <Button
              variant="outlined"
              onClick={() => {
                setFieldErrors((prev) => ({ ...prev, password: "" }));
                setPassword2("");
                setShowPassword(false);
                setOpenPassword(true);
              }}
              type="button"
              sx={{
                color: "#111",
                borderColor: "#dfdfdf",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { borderColor: "#f7b900", bgcolor: "#fffaf0" },
              }}
            >
              Change password
            </Button>
          </Card>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Button variant="contained" type="submit" sx={yellowButtonSx}>
              Save
            </Button>

            <Button
              color="error"
              variant="contained"
              startIcon={<DeleteOutline />}
              onClick={() => setOpenDelete(true)}
              type="button"
              sx={{
                minHeight: 44,
                px: 2.5,
                bgcolor: "#dc2626",
                color: "#fff",
                textTransform: "none",
                fontWeight: 800,
                boxShadow: "none",
                "&:hover": { bgcolor: "#b91c1c" },
              }}
            >
              Delete account
            </Button>
          </Stack>
        </Box>

        <Dialog
          open={openPassword}
          onClose={() => setOpenPassword(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 800 }}>Change password</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter a new password for your account.
            </DialogContentText>
            <TextField
              label="New password"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={password2}
              onChange={(e) => {
                setPassword2(e.target.value);
                setFieldErrors((prev) => ({ ...prev, password: "" }));
              }}
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password}
              sx={{
                  ...authFieldSx,
                  "& .MuiInputLabel-root": {
                    color: "#000",
                  },
                }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword((value) => !value)}
                    edge="end"
                    sx={passwordToggleSx}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setOpenPassword(false)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handlePasswordSave} sx={yellowButtonSx}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <DeleteConfirmDialog
          open={openDelete}
          title="Do you want to delete your account?"
          description="Are you sure you want to delete your account? This action is permanent and cannot be undone."
          onClose={() => setOpenDelete(false)}
          onConfirm={handleDelete}
        />

        <AppSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        />
      </Box>
    </Container>
  );
}
